import { TFunction } from 'i18next';

import { Maybe } from '../../types';
import { numberToWord } from '../../utils';
import {
  IntersessionSummary__FollowUpPreVisitSummaryContent,
  IntersessionSummary__IntakePreVisitSummaryContent,
} from './types';

export function formatIntakeHeader(t: TFunction, firstName: string, coachTopics: string[]): string {
  const hasCoachTopics = coachTopics.length > 0;

  if (!hasCoachTopics) {
    return t('telehealth-core:providerIntersessionSummary.noPreIntakeActivity', {
      firstName: firstName,
    });
  }
  const topicsText = coachTopics.slice(0, 2).join(' and ').toLocaleLowerCase();

  return t('telehealth-core:providerIntersessionSummary.beforeIntakeEngagedWithCoach', {
    firstName: firstName,
    topics: topicsText,
  });
}

export function formatFollowupHeader(
  t: TFunction,
  firstName: string,
  pronouns: string,
  coachTopics: string[],
  numCompletedResources: number,
  reflectionTopics: string[],
): string {
  const hasCoachTopics = coachTopics.length > 0;
  const hasCompletedResources = numCompletedResources && numCompletedResources > 0;
  const hasReflectionTopics = reflectionTopics.length > 0;

  if (!hasCoachTopics && !hasCompletedResources && !hasReflectionTopics) {
    return t('telehealth-core:providerIntersessionSummary.sinceLastSessionXHasntSharedAnyUpdates', {
      firstName: firstName,
    });
  }

  let header = '';
  if (hasCoachTopics || hasCompletedResources) {
    header += t('telehealth-core:providerIntersessionSummary.sinceLastSessionX', {
      firstName: firstName,
    });
  }

  if (hasCoachTopics) {
    const topicsText = coachTopics.slice(0, 2).join(' and ').toLocaleLowerCase();
    header += ` ${t('telehealth-core:providerIntersessionSummary.engagedWithCoachOnTopics', {
      topics: topicsText,
    })}`;
  }

  if (hasCoachTopics && hasCompletedResources) {
    header += ` ${t('telehealth-core:core.and')} `;
  }

  if (hasCompletedResources) {
    header += ` ${t('telehealth-core:providerIntersessionSummary.interactedWithXResources', {
      count: numCompletedResources,
      countWord: numberToWord(numCompletedResources, t),
    })}`;
  }

  if (hasCoachTopics || hasCompletedResources) {
    header += '.';
  }

  if (hasReflectionTopics) {
    const formattedTopics = reflectionTopics.slice(0, 2).join(' and ').toLocaleLowerCase();
    const pronoun = pronouns?.split('/')[0] ?? 'they';
    header += ` ${t('telehealth-core:providerIntersessionSummary.thisSessionCover', {
      pronoun,
      topics: formattedTopics,
    })}`;
  }

  return header;
}

export function formatBetweenSessionAction(
  t: TFunction,
  hasCoachAccess: boolean,
  coachTopics: string[],
  numCompletedResources: number,
): string {
  const hasCoachTopics = coachTopics.length > 0;
  if (hasCoachAccess) {
    if (!hasCoachTopics && !numCompletedResources) {
      return t('telehealth-core:providerIntersessionSummary.noCoachInteractionsOrResourceActivity');
    }

    if (!hasCoachTopics) {
      return t('telehealth-core:providerIntersessionSummary.noCoachInteractionsInteractedWithXResources', {
        count: numCompletedResources,
        countWord: numberToWord(numCompletedResources, t),
      });
    }

    const topicsText = coachTopics.slice(0, 2).join(' and ').toLocaleLowerCase();

    if (numCompletedResources) {
      return t('telehealth-core:providerIntersessionSummary.discussedTopicsInteractedWithXResources', {
        topics: topicsText,
        count: numCompletedResources,
        countWord: numberToWord(numCompletedResources, t),
      });
    }
    return t('telehealth-core:providerIntersessionSummary.discussedTopics', { topics: topicsText });
  }

  if (numCompletedResources) {
    return t('telehealth-core:providerIntersessionSummary.interactedWithXResourcesSinceLastSession', {
      count: numCompletedResources,
      countWord: numberToWord(numCompletedResources, t),
    });
  }

  return t('telehealth-core:providerIntersessionSummary.noResourceActivity');
}

export function formatMeasureTitle(
  t: TFunction,
  hasOutstandingMeasures: boolean,
  content?: Maybe<
    IntersessionSummary__FollowUpPreVisitSummaryContent | IntersessionSummary__IntakePreVisitSummaryContent
  >,
): string {
  if (hasOutstandingMeasures) {
    return t('telehealth-core:providerIntersessionSummary.measuresNotCompleted');
  }

  const hasCurrentScores = content?.currentGadCategory || content?.currentPhqCategory;
  if (!content || !hasCurrentScores) {
    return t('telehealth-core:providerIntersessionSummary.noNewScoresToReport');
  }

  const isIntake = content.__typename === 'IntakePreVisitSummaryContentObject';
  if (isIntake) {
    return t('telehealth-core:providerIntersessionSummary.baselineMeasures');
  }

  const hasPreviousScores = content.previousGadCategory || content.previousPhqCategory;
  if (!hasPreviousScores) {
    return t('telehealth-core:providerIntersessionSummary.baselineMeasures');
  }

  const hasGad = content.currentGad && content.currentGadCategory;
  const hasPhq = content.currentPhq && content.currentPhqCategory;
  const gadUnchanged = content.currentGad === content.previousGad;
  const phqUnchanged = content.currentPhq === content.previousPhq;

  if (hasGad && gadUnchanged && hasPhq && phqUnchanged) {
    return t('telehealth-core:providerIntersessionSummary.scoresConsistent', {
      currentPhqCategory: content.currentPhqCategory,
      currentPhq: content.currentPhq,
      currentGadCategory: content.currentGadCategory,
      currentGad: content.currentGad,
    });
  }
  if (!hasGad && phqUnchanged) {
    return t('telehealth-core:providerIntersessionSummary.scoreIsConsistentDepression', {
      currentPhqCategory: content.currentPhqCategory,
      currentPhq: content.currentPhq,
    });
  }
  if (!hasPhq && gadUnchanged) {
    return t('telehealth-core:providerIntersessionSummary.scoreIsConsistentAnxiety', {
      currentGadCategory: content.currentGadCategory,
      currentGad: content.currentGad,
    });
  }

  const measureDescriptions: string[] = [];

  if (hasGad) {
    const gadScoreChanged = content.currentGad !== content.previousGad;
    const gadCategoryChanged = content.currentGadCategory !== content.previousGadCategory;

    if (!gadScoreChanged) {
      measureDescriptions.push(
        t('telehealth-core:providerIntersessionSummary.categoryAnxietyGad7Score', {
          category: content.currentGadCategory,
          score: content.currentGad,
        }),
      );
    } else if (!gadCategoryChanged) {
      measureDescriptions.push(
        t('telehealth-core:providerIntersessionSummary.categoryAnxietyGad7ChangedFromXScoreToYScore', {
          category: content.currentGadCategory,
          previousGad: content.previousGad,
          currentGad: content.currentGad,
        }),
      );
    } else {
      measureDescriptions.push(
        t('telehealth-core:providerIntersessionSummary.anxietyChangedFromXCategoryToYCategory', {
          previousGadCategory: content.previousGadCategory,
          currentGadCategory: content.currentGadCategory,
          previousGad: content.previousGad,
          currentGad: content.currentGad,
        }),
      );
    }
  }

  if (hasPhq) {
    const phqScoreChanged = content.currentPhq !== content.previousPhq;
    const phqCategoryChanged = content.currentPhqCategory !== content.previousPhqCategory;

    if (!phqScoreChanged) {
      measureDescriptions.push(
        t('telehealth-core:providerIntersessionSummary.categoryDepressionPhq9Score', {
          category: content.currentPhqCategory,
          score: content.currentPhq,
        }),
      );
    } else if (!phqCategoryChanged) {
      measureDescriptions.push(
        t('telehealth-core:providerIntersessionSummary.categoryDepressionPhq9ChangedFromXScoreToYScore', {
          category: content.currentPhqCategory,
          previousPhq: content.previousPhq,
          currentPhq: content.currentPhq,
        }),
      );
    } else {
      measureDescriptions.push(
        t('telehealth-core:providerIntersessionSummary.depressionChangedFromXCategoryToYCategory', {
          previousPhqCategory: content.previousPhqCategory,
          currentPhqCategory: content.currentPhqCategory,
          previousPhq: content.previousPhq,
          currentPhq: content.currentPhq,
        }),
      );
    }
  }

  const separator = ` ${t('telehealth-core:core.and')} `;
  const prefix =
    hasGad && hasPhq
      ? t('telehealth-core:providerIntersessionSummary.scoresHaveChanged')
      : t('telehealth-core:providerIntersessionSummary.scoreHasChanged');
  return `${prefix} ${measureDescriptions.join(separator)}`;
}
